import { useState } from "react";

import type { OptionType } from "react-lib/apps/HISV3/common/CommonInterface";
import formatComma from "react-lib/utils/formatComma";
import { type OnEventHandler } from "react-lib/appcon/common/SearchBoxDropdown";

import { ButtonV4, SelectV4, type ChipV4Tone, type SelectV4Option } from "../common/ui";
import {
  PERFORMERS,
  SUPPLY_USES,
  type DoctorOption,
  type DrugMaster,
  type Icd9Option,
  type OrderMaster,
  type OrderMasterOptions,
  type SheetLine,
} from "../common/sequence/OrderSheetV4";
import {
  DRUG_PRIORITIES,
  IMAGING_SIDES,
  PREGNANCY,
  URGENCY_LABELS,
  supplyTypeOf,
} from "./ordersModelV4";

// Types
type Field = {
  key: string;
  label: string;
  width: number | "full" | "grow";
  required?: boolean;
  placeholder?: string;
  options?: [string, string][];
  /** ช่องค้นหาที่เลือกจากรายการ */
  combo?: boolean;
  /** ตัวเลือกจาก master */
  master?: OrderMaster;
  /** พิมพ์เองได้ มีรายการแนะนำจาก master */
  suggest?: OrderMaster;
  /** บังคับเมื่อยาตั้ง require_<ค่านี้> */
  requiredBy?: DrugMaster;
  numeric?: boolean;
  maxLength?: number;
  /** แสดงเมื่อ */
  when?: (fields: Record<string, string>, raw: SheetLine["raw"]) => boolean;
};

type SheetLineFieldsProps = {
  line: SheetLine;
  doctorOptions: DoctorOption[];
  masterOptions?: OrderMasterOptions;
  icd9Options: Icd9Option[];
  searchedItemListWithKey?: Record<string, any>;
  // callback
  onEvent: OnEventHandler;
  onChange: (fields: Record<string, string>) => void;
  onSearchIcd9: (query: string) => void;
  onClose: () => void;
};

type SheetLineControlsProps = {
  line: SheetLine;
  // callback
  onChange: (data: {
    quantity?: number;
    priceUnit?: number;
    fields?: Record<string, string>;
  }) => void;
};

// Const
const FIELDS: Record<SheetLine["kind"], Field[]> = {
  imaging: [
    { key: "side", label: "ตำแหน่ง / ข้าง", width: 96, options: IMAGING_SIDES.map((v) => [v, v]) },
    { key: "view", label: "ท่า / Views", width: 150, placeholder: "เช่น AP, Lateral" },
    { key: "urgency", label: "ความเร่งด่วน", width: 100, options: Object.entries(URGENCY_LABELS) },
    { key: "preg", label: "การตั้งครรภ์", width: 130, options: PREGNANCY.map((v) => [v, v]) },
    {
      key: "ind",
      label: "Clinical indication",
      width: "full",
      required: true,
      placeholder: "เหตุผลทางคลินิกสำหรับรังสีแพทย์",
    },
  ],
  treatment: [
    {
      key: "performer",
      label: "ผู้ทำหัตถการ (บทบาท)",
      width: 150,
      required: true,
      placeholder: "ไม่ระบุ",
      options: PERFORMERS,
    },
    {
      key: "icd",
      label: "ICD-9-CM",
      width: 300,
      placeholder: "ค้นหารหัส / ชื่อหัตถการ",
      combo: true,
    },
    { key: "detail", label: "รายละเอียด", width: 240, placeholder: "เช่น หลังส่วนล่าง · ข้างซ้าย" },
    {
      key: "equipment",
      label: "อุปกรณ์ที่ใช้",
      width: "full",
      placeholder: "เช่น เข็ม 0.25×40 mm · ชุดทำแผล",
    },
  ],
  drug: [
    { key: "route", label: "Route", width: 150, master: "route" },
    { key: "site", label: "Site", width: 150, master: "site" },
    {
      key: "dose",
      label: "Dose (ครั้งละ)",
      width: 110,
      required: true,
      requiredBy: "unit",
      numeric: true,
    },
    { key: "unit", label: "Unit", width: 120, master: "unit" },
    { key: "frequency", label: "Frequency", width: 220, required: true, master: "frequency" },
    { key: "prn", label: "PRN (กินเมื่อ…)", width: 240, suggest: "prn" },
    {
      key: "duration",
      label: "จำนวนวัน",
      width: 100,
      required: true,
      numeric: true,
      when: (fields, raw) => fields.home === "Y" && !raw.asNeeded,
    },
    { key: "qty", label: "QTY (พิมพ์เองได้)", width: 130, numeric: true },
    { key: "description", label: "Description", width: "full", maxLength: 60 },
  ],
  supply: [
    {
      key: "use",
      label: "รูปแบบการใช้ (กรณีใช้ที่คลินิก)",
      width: "grow",
      options: SUPPLY_USES,
      when: (fields) => fields.home !== "Y",
    },
    {
      key: "costCenter",
      label: "Cost center ที่รับต้นทุน",
      width: "grow",
      required: true,
      master: "costCenter",
      when: (fields) => fields.home !== "Y" && fields.use === "COST_CENTER",
    },
    { key: "note", label: "หมายเหตุ", width: "full" },
  ],
  note: [
    {
      key: "detail",
      label: "ข้อความคำสั่ง (Free text · ไม่มีค่าใช้จ่าย)",
      width: "full",
      required: true,
      placeholder: "เช่น วัดความดันซ้ำหลังพัก 15 นาที · แจ้งแพทย์ถ้า > 160/100",
    },
    {
      key: "reason",
      label: "เหตุผลที่ดำเนินการแทนแพทย์",
      width: "full",
      required: true,
      when: (fields) => fields.onBehalf === "Y",
    },
  ],
  df: [
    {
      key: "employee",
      label: "แพทย์ผู้รับ DF",
      width: "full",
      required: true,
      placeholder: "ค้นหาชื่อแพทย์",
    },
  ],
  misc: [
    {
      key: "note",
      label: "รายละเอียด",
      width: "full",
      placeholder: "ระบุรายละเอียดของรายการ เช่น ค่าถ่ายเอกสาร 5 ชุด",
    },
  ],
};

const QTY_KINDS = new Set<SheetLine["kind"]>(["imaging", "treatment", "supply", "misc"]);

const PRICE_KINDS = new Set<SheetLine["kind"]>(["df", "misc"]);

/* ------------------------------------------------------ */

/*                          Utils                         */

/* ------------------------------------------------------ */
const visibleFields = (line: SheetLine) =>
  FIELDS[line.kind]
    .filter((field) => !field.when || field.when(line.fields, line.raw))
    .map((field) => {
      const detail = line.raw.detail;
      if (field.requiredBy && detail) {
        return { ...field, required: !!detail[`require_${field.requiredBy}`] };
      }

      return field.master && detail?.[`require_${field.master}`]
        ? { ...field, required: true }
        : field;
    });

const LIMITED_KEYS: Partial<Record<OrderMaster, string>> = {
  unit: "limited_dosage_units",
  route: "limited_routes",
  site: "limited_sites",
  frequency: "limited_frequencies",
};

/** ตัวเลือกใน master (ยาที่จำกัดไว้ = เฉพาะที่จำกัด) */
const masterOptionsOf = (
  line: SheetLine,
  master: OrderMaster,
  masterOptions?: OrderMasterOptions,
) => {
  const limited: unknown[] = line.raw.detail?.[LIMITED_KEYS[master] ?? ""] ?? [];
  const allowed = new Set([...limited.map(String), line.fields[master]]);

  return (masterOptions?.[master] ?? []).filter(
    (option) => limited.length <= 0 || allowed.has(String(option.value)),
  );
};

const FACT_ROWS: [string, string][] = [
  ["drug_using", "วิธีใช้ยา"],
  ["suggestions", "ข้อแนะนำ"],
  ["cautions", "ข้อควรระวัง"],
  ["warnings", "คำเตือน"],
  ["others", "ข้อมูลอื่นๆ"],
];

const drugFactText = (line: SheetLine) => {
  const factsheet: Record<string, string> = line.raw.detail?.factsheet_data ?? {};
  const rows = FACT_ROWS.filter(([key]) => factsheet[key]?.trim()).map(
    ([key, label]) => `${label}: ${factsheet[key].trim()}`,
  );

  return rows.length > 0 ? `ข้อมูลยา: ${rows.join(" · ")}` : "ยานี้ไม่มีข้อมูลยาใน Master";
};

const isQtyMissing = (line: SheetLine) => line.kind === "drug" && line.quantity <= 0;

const isPriceMissing = (line: SheetLine) =>
  line.kind === "misc" && !line.masterPrice && line.priceUnit <= 0;

const isIcdUnpicked = (line: SheetLine) =>
  line.kind === "treatment" && !!line.fields.icd?.trim() && !line.fields.icd9;

export const sheetMissing = (line: SheetLine): string[] => [
  ...visibleFields(line)
    .filter((field) => field.required && !String(line.fields[field.key] ?? "").trim())
    .map((field) => field.label),
  ...(isIcdUnpicked(line) ? ["ICD-9-CM (เลือกจากรายการ)"] : []),
  ...(isQtyMissing(line) ? ["QTY"] : []),
  ...(isPriceMissing(line) ? ["ราคา/หน่วย"] : []),
];

export const isCostCenter = (line: SheetLine) =>
  line.kind === "supply" && supplyTypeOf(line) === "COST_CENTER";

export const sheetAmount = (line: SheetLine) =>
  isCostCenter(line) ? 0 : line.priceUnit * line.quantity;

const labelOf = (options: [string, string][], value: string) =>
  options.find(([key]) => key === value)?.[1] ?? value;

const optionText = (options: OptionType[] | undefined, value: string) =>
  options?.find((option) => String(option.value) === value)?.text ?? "";

export const sheetSummary = (
  line: SheetLine,
  doctorOptions: DoctorOption[],
  masterOptions?: OrderMasterOptions,
): string => {
  const fields = line.fields;

  if (line.kind === "imaging") {
    return [
      URGENCY_LABELS[fields.urgency] ?? fields.urgency,
      fields.side === "-" ? "" : fields.side,
      fields.view,
      fields.ind,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "treatment") {
    return [
      fields.performer ? labelOf(PERFORMERS, fields.performer) : "",
      fields.icd9 ? fields.icd : "",
      fields.detail,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "drug") {
    const label = (master: DrugMaster) => optionText(masterOptions?.[master], fields[master]);

    return [
      fields.priority === "STAT" ? "STAT" : "",
      fields.dose ? `ครั้งละ ${fields.dose} ${label("unit")}`.trim() : "",
      label("route"),
      label("site"),
      label("frequency"),
      fields.prn,
      line.raw.asNeeded || fields.home !== "Y" || !fields.duration ? "" : `${fields.duration} วัน`,
      line.quantity > 0 ? `QTY ${line.quantity}` : "",
      fields.description,
    ]
      .filter(Boolean)
      .join(" · ");
  }

  if (line.kind === "df") {
    const doctor = doctorOptions.find((item) => String(item.employee) === fields.employee);

    return doctor?.name ?? "";
  }

  if (line.kind === "supply") {
    if (fields.home === "Y") {
      return "จ่ายกลับบ้าน · เก็บเงิน";
    }

    if (!isCostCenter(line)) {
      return "ใช้ที่คลินิก · Refill เก็บเงินเพิ่ม";
    }

    const costCenter = optionText(masterOptions?.costCenter, fields.costCenter);
    const cost = formatComma(line.priceUnit * line.quantity, "en-US", false);

    return [
      `ใช้ที่คลินิก · Cost center ${costCenter}`.trim(),
      `ไม่เก็บเงินเพิ่ม (ต้นทุน ${cost} ฿)`,
    ].join(" · ");
  }

  return fields.detail ?? fields.note ?? "";
};

export const sheetMode = (line: SheetLine): [string, ChipV4Tone] | null => {
  if (line.kind !== "supply" && line.kind !== "drug") {
    return null;
  }

  if (line.fields.home === "Y") {
    return ["กลับบ้าน", "sky"];
  }

  if (line.kind === "drug") {
    return ["ใช้ที่คลินิก", "grey"];
  }

  return isCostCenter(line) ? ["Cost center · เหมารวม", "ok"] : ["Refill · เก็บเงิน", "warn"];
};

/* ------------------------------------------------------ */

/*                       Components                       */

/* ------------------------------------------------------ */
export const SheetLineControls = (props: SheetLineControlsProps) => {
  const { line } = props;

  return (
    <>
      {(line.kind === "supply" || line.kind === "drug") && (
        <label className={`hv4-ord-home${line.fields.home === "Y" ? " hv4-ord-home-on" : ""}`}>
          <input
            type="checkbox"
            checked={line.fields.home === "Y"}
            disabled={line.fields.priority === "STAT"}
            onChange={(e) => props.onChange({ fields: { home: e.target.checked ? "Y" : "N" } })}
          />
          จ่ายกลับบ้าน
        </label>
      )}
      {QTY_KINDS.has(line.kind) && (
        <span className="hv4-ord-qty">
          <ButtonV4
            variant="ghost"
            size="icon"
            aria-label="ลดจำนวน"
            disabled={line.quantity <= 1}
            onClick={() => props.onChange({ quantity: line.quantity - 1 })}
          >
            −
          </ButtonV4>
          <b>{line.quantity}</b>
          <ButtonV4
            variant="ghost"
            size="icon"
            aria-label="เพิ่มจำนวน"
            onClick={() => props.onChange({ quantity: line.quantity + 1 })}
          >
            +
          </ButtonV4>
        </span>
      )}
      {PRICE_KINDS.has(line.kind) && (
        <span className="hv4-ord-price">
          {line.kind === "df" && (
            <span className="hv4-ord-price-hint">ราคา Master {line.masterPrice} ฿</span>
          )}
          ราคา/หน่วย
          <input
            className="hv4-input"
            aria-label="ราคาต่อหน่วย (บาท)"
            inputMode="decimal"
            value={line.priceUnit}
            onChange={(e) => props.onChange({ priceUnit: Number(e.target.value) || 0 })}
          />
        </span>
      )}
    </>
  );
};

export const SheetLineFields = (props: SheetLineFieldsProps) => {
  const { line } = props;

  const [info, setInfo] = useState<"fact" | "solvent" | null>(null);

  const solvents: string[] = line.raw.solvents ?? [];

  const selectOptions = (field: Field): SelectV4Option[] =>
    field.master
      ? masterOptionsOf(line, field.master, props.masterOptions)
      : (field.options ?? []).map(([value, text]) => ({ value, text }));

  const handleChange = (key: string, value: string) => {
    if (key !== "icd") {
      props.onChange({ [key]: value });

      return;
    }

    const picked = props.icd9Options.find((option) => option.label === value);

    props.onChange({ icd: value, icd9: picked ? JSON.stringify(picked) : "" });

    if (!picked) {
      props.onSearchIcd9(value);
    }
  };

  return (
    <div className="hv4-ord-fields">
      {line.kind === "drug" && (
        <div className="hv4-ord-priority">
          <label className="hv4-label" htmlFor={`hv4_ord_${line.key}_priority`}>
            Priority
          </label>
          <SelectV4
            id={`hv4_ord_${line.key}_priority`}
            size="sm"
            options={DRUG_PRIORITIES.map(([value, text]) => ({ value, text }))}
            value={line.fields.priority || "ROUTINE"}
            onChange={(next) =>
              props.onChange(next === "STAT" ? { priority: next, home: "N" } : { priority: next })
            }
          />
        </div>
      )}
      <div className="hv4-ord-fields-row">
        {visibleFields(line).map((field) => {
          const id = `hv4_ord_${line.key}_${field.key}`;
          const value = line.fields[field.key] ?? "";
          const isEmpty =
            (!!field.required && !value.trim()) || (field.key === "icd" && isIcdUnpicked(line));
          const options = field.options || field.master ? selectOptions(field) : null;

          return (
            <div
              key={field.key}
              className={
                typeof field.width === "number"
                  ? "hv4-ord-field"
                  : `hv4-ord-field hv4-ord-field-${field.width}`
              }
              style={typeof field.width === "number" ? { width: field.width } : undefined}
            >
              <label className="hv4-label" htmlFor={id}>
                {field.required ? `${field.label} *` : field.label}
                {!!field.maxLength && ` [${value.length}/${field.maxLength}]`}
              </label>
              {field.key === "employee" ? (
                <div className={`hv4-search-dd${isEmpty ? " hv4-search-dd-invalid" : ""}`}>
                  <SelectV4
                    id={id}
                    search
                    options={props.doctorOptions.map((doctor) => ({ value: doctor.employee, text: doctor.name }))}
                    value={props.doctorOptions.some((doctor) => String(doctor.employee) === value) ? value : ""}
                    placeholder={field.placeholder}
                    onChange={(next) => handleChange(field.key, next)}
                  />
                </div>
              ) : field.suggest ? (
                <div className="hv4-ord-combo">
                  <input
                    id={id}
                    className="hv4-input hv4-input-sm"
                    list={`${id}_list`}
                    aria-invalid={isEmpty}
                    value={value}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                  />
                  <datalist id={`${id}_list`}>
                    {(props.masterOptions?.[field.suggest] ?? []).map((option) => (
                      <option key={String(option.value)} value={option.text} />
                    ))}
                  </datalist>
                  {!!value && (
                    <ButtonV4
                      variant="ghost"
                      size="icon"
                      aria-label="ล้างค่า"
                      onClick={() => handleChange(field.key, "")}
                    >
                      ✕
                    </ButtonV4>
                  )}
                </div>
              ) : options ? (
                <SelectV4
                  id={id}
                  size="sm"
                  search={options.length > 8}
                  invalid={isEmpty}
                  placeholder={field.placeholder ?? "— เลือก —"}
                  options={options}
                  value={value}
                  onChange={(next) => handleChange(field.key, next)}
                />
              ) : field.combo ? (
                <div className="hv4-ord-combo">
                  <SelectV4
                    id={id}
                    size="sm"
                    search
                    invalid={isEmpty}
                    placeholder={field.placeholder}
                    options={props.icd9Options.map((option) => ({
                      value: option.label,
                      text: option.label,
                    }))}
                    value={value}
                    onSearch={props.onSearchIcd9}
                    onChange={(next) => handleChange(field.key, next)}
                  />
                  {!!value && (
                    <ButtonV4
                      variant="ghost"
                      size="icon"
                      aria-label="ล้างค่า"
                      onClick={() => handleChange(field.key, "")}
                    >
                      ✕
                    </ButtonV4>
                  )}
                </div>
              ) : (
                <input
                  id={id}
                  className="hv4-input hv4-input-sm"
                  aria-invalid={isEmpty || (field.key === "qty" && isQtyMissing(line))}
                  inputMode={field.numeric ? "decimal" : undefined}
                  maxLength={field.maxLength}
                  placeholder={
                    field.key === "qty" ? `auto ${line.quantity || 0}` : field.placeholder
                  }
                  value={value}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
      {line.kind === "drug" && (
        <div className="hv4-ord-drug-info">
          <div className="hv4-ord-drug-info-btns">
            <ButtonV4
              variant="ghost"
              size="sm"
              className="hv4-ord-fact-btn"
              selected={info === "fact"}
              onClick={() => setInfo(info === "fact" ? null : "fact")}
            >
              DRUG FACT
            </ButtonV4>
            <ButtonV4
              variant="ghost"
              size="sm"
              className="hv4-ord-solv-btn"
              selected={info === "solvent"}
              onClick={() => setInfo(info === "solvent" ? null : "solvent")}
            >
              SOLVENT
            </ButtonV4>
          </div>
          {info === "fact" && <div className="hv4-ord-fact">{drugFactText(line)}</div>}
          {info === "solvent" && (
            <div className="hv4-ord-solv">
              <b>{solvents.length > 0 ? "Solvent ที่แนะนำ: " : "ยานี้ไม่มี Solvent ใน Master"}</b>
              {solvents.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="hv4-ord-solv-pick"
                  onClick={() => props.onChange({ description: `Solvent: ${name}`.slice(0, 60) })}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="hv4-ord-fields-foot">
        <ButtonV4 variant="primary" onClick={props.onClose}>
          บันทึก
        </ButtonV4>
      </div>
    </div>
  );
};
